import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Rating,
  Button,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  LocalDining as DiningIcon,
  LocationOn as MapPinIcon,
  Spa as VegetarianIcon,
  Whatshot as SpicyIcon,
} from '@mui/icons-material';
import api, { formatGBP, getImageUrl } from '../services/api';
import Seo from '../components/Seo';

const RestaurantDynamic = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [menuItems, setMenuItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'all', name: 'All Items' },
    { id: 'Appetizers', name: 'Appetizers' },
    { id: 'Main Course', name: 'Main Course' },
    { id: 'Desserts', name: 'Desserts' },
    { id: 'Beverages', name: 'Beverages' },
  ];

  const cuisines = [
    { id: 'all', name: 'All Cuisines' },
    { id: 'Pakistani', name: 'Pakistani Cuisine' },
    { id: 'Continental', name: 'Continental' },
    { id: 'Chinese', name: 'Chinese' },
    { id: 'Italian', name: 'Italian' },
    { id: 'Thai', name: 'Thai' }
  ];

  const locations = [
    { id: 'all', name: 'All Locations' },
    { id: 'Islamabad', name: 'Islamabad' },
    { id: 'Lahore', name: 'Lahore' },
    { id: 'Karachi', name: 'Karachi' }
  ];

  useEffect(() => {
    fetchRestaurantData();
  }, []);

  const fetchRestaurantData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/RestaurantLocation');
      const restaurants = response.data || [];
      
      // Fetch menu items for all restaurants
      const menuResponse = await api.get('/MenuItem');
      const items = menuResponse.data || [];
      console.log('🍽️ Menu Items API Response:', items);
      console.log('🍽️ First item:', items[0]);
      
      // Transform API data
      const transformedItems = items.map(item => {
        console.log('🔍 Transforming item:', item.name, 'imagePath:', item.imagePath);
        const imageUrl = getImageUrl(item.imagePath || item.ImagePath || 'chicken-tikka.jpg', 'restaurant');
        console.log('🖼️ Final image URL:', imageUrl);
        return {
          id: item.menuItemID,
          name: item.name || 'Menu Item',
          category: item.category || 'Main Course',
          cuisine: item.cuisine || 'Pakistani',
          price: item.price || 0,
          description: item.description || '',
          image: imageUrl,
          rating: item.rating || 4.5,
          spicy: item.isSpicy || false,
          vegetarian: item.isVegetarian || false,
          locations: item.restaurantID ? [restaurants.find(r => r.restaurantID === item.restaurantID)?.location || 'Islamabad'] : ['Islamabad'],
          displayOrder: item.displayOrder
        };
      });

      transformedItems.sort((a, b) => a.displayOrder - b.displayOrder);
      
      setMenuItems(transformedItems);
      
      setRestaurants(
        restaurants.map((restaurant, idx) => ({
          id: restaurant.restaurantID || restaurant.id || idx + 1,
          name: restaurant.name || restaurant.restaurantName || `${restaurant.location || 'Restaurant'} Restaurant`,
          location: restaurant.location || 'Restaurant Location',
          rating: restaurant.rating || 4.8,
          image: getImageUrl(
            restaurant.imagePath || restaurant.ImagePath || restaurant.photo || transformedItems.find(item => item.locations.includes(restaurant.location))?.image || 'default-restaurant.jpg',
            'restaurant'
          )
        }))
      );
      
      setError(null);
    } catch (err) {
      console.error('Error fetching restaurant data:', err);
      setError('Failed to load restaurant menu');
    } finally {
      setLoading(false);
    }
  };

  const filteredMenuItems = menuItems.filter(item => {
    const categoryMatch = selectedCategory === 'all' || item.category === selectedCategory;
    const locationMatch = selectedLocation === 'all' || item.locations.includes(selectedLocation);
    return categoryMatch && locationMatch;
  });

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <Seo
        title="Restaurant"
        description="Dine at Luxury Hotel with curated Pakistani and international flavors. Explore our menu, locations, and signature dishes for every occasion."
        keywords="Luxury Hotel restaurant, hotel dining, Pakistani cuisine, continental food, menu, breakfast, lunch, dinner"
        author="Luxury Hotel"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2c5aa0 0%, #1e3a6f 50%, #0f3460 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Playfair Display',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                background: 'linear-gradient(45deg, #ffffff 30%, #d4af37 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Restaurant & Dining
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto', lineHeight: 1.6 }}>
              Experience culinary excellence with our diverse menu featuring the finest
              Pakistani, Continental, and Chinese cuisines prepared by master chefs.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Our Restaurants Section */}
      {restaurants.length > 0 && (
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Playfair Display',
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
              textAlign: 'center',
              mb: 6,
              color: '#111827'
            }}
          >
            Our Restaurants
          </Typography>
          
          <Grid container spacing={4}>
            {restaurants.map((restaurant) => (
              <Grid item xs={12} md={4} key={restaurant.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="240"
                    image={restaurant.image}
                    alt={restaurant.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h5" fontWeight="700" gutterBottom>
                      {restaurant.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Rating value={restaurant.rating} precision={0.1} readOnly size="small" />
                      <Typography variant="body2" color="text.secondary">
                        {restaurant.rating}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                      <MapPinIcon fontSize="small" />
                      <Typography variant="body2">{restaurant.location}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Our Menu Section */}
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Playfair Display',
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            textAlign: 'center',
            mb: 6,
            color: '#111827'
          }}
        >
          Our Menu
        </Typography>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 6, borderRadius: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="600" mb={1}>
                Filter by Category
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {categories.map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    onClick={() => setSelectedCategory(category.id)}
                    color={selectedCategory === category.id ? 'primary' : 'default'}
                    variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="600" mb={1}>
                Filter by Location
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {locations.map((location) => (
                  <Chip
                    key={location.id}
                    label={location.name}
                    onClick={() => setSelectedLocation(location.id)}
                    color={selectedLocation === location.id ? 'primary' : 'default'}
                    variant={selectedLocation === location.id ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Menu Items Grid */}
        <Grid container spacing={4}>
          {filteredMenuItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={item.image}
                    alt={item.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={item.cuisine}
                      size="small"
                      sx={{
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                    {item.vegetarian && (
                      <Chip
                        icon={<VegetarianIcon />}
                        label="Veg"
                        size="small"
                        sx={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                </Box>
                
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h6" fontWeight="700" gutterBottom>
                    {item.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Rating value={item.rating} precision={0.1} readOnly size="small" />
                    <Typography variant="body2" color="text.secondary">
                      {item.rating}
                    </Typography>
                    {item.spicy && (
                      <Chip
                        icon={<SpicyIcon />}
                        label="Spicy"
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {item.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                    <Typography variant="h6" fontWeight="700" color="primary">
                      {formatGBP(item.price)}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        backgroundColor: '#2563eb',
                        '&:hover': { backgroundColor: '#1d4ed8' },
                      }}
                    >
                      Order Now
                    </Button>
                  </Box>
                  
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                    <Typography variant="caption" color="text.secondary">
                      <MapPinIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Available at: {item.locations.join(', ')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredMenuItems.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No menu items found for the selected filters.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default RestaurantDynamic;
